import { createContext, useContext, useId, useState } from "react";
import { Form, Link } from "react-router";
import { useAccountLink } from "@/components/navigation";
import { Check } from "lucide-react";
import type { Entry, EntryPart, ResultScore } from "@repo/shared-types";
import {
  METRIC_DISTANCE_UNITS,
  racesTheCap,
  scoreFields,
  type Measured,
} from "@/lib/measures";
import {
  FEELS,
  LEVELS,
  divisionLabel,
  feelEmoji,
  isScored,
  levelLabel,
  ordinal,
  type RecordablePart,
} from "@/lib/results";
import { formatMoment } from "@/lib/times";
import { newTeamId, type TeamPeople } from "@/lib/teams";
import { TeamPeopleBox, teamScore } from "@/components/team-fields";
import { FeelAndNote } from "@/components/feel-and-note";
import { ProblemNote, SegmentedControl, useProblem } from "@/components/form";
import { ConfirmDialog } from "@/components/modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Toggle } from "@/components/ui/toggle";

/**
 * The result form and a result as its athlete reads it.
 *
 * Uncontrolled, like every form in this app: the router's action reads the
 * boxes, named as src/lib/results.ts and src/lib/measures.ts expect. Nobody
 * types a unit or a format — each measure has a box per number and, where it
 * has one, a unit picked beside them. The only state here is which boxes to
 * show: a capped time's reps, a distance in feet's inches.
 */

/**
 * The form a part's boxes belong to, by its id: a part's dialog, wherever the
 * boxes sit inside it. A box with none — the workout form's — belongs to the
 * form around it.
 */
const FormOwner = createContext<string | undefined>(undefined);

type BoxesProps = {
  prefix: string;
  type: Measured;
  position: number;
  timeLimitMs: number | null;
  current: ResultScore | undefined;
};

/** The boxes one score is typed in, by what it measures — outlined, and why said under them, when refused. */
export function ScoreBoxes(props: BoxesProps) {
  return (
    <>
      <MeasureBoxes {...props} />
      <ProblemNote name={props.prefix} />
    </>
  );
}

function MeasureBoxes(props: BoxesProps) {
  switch (props.type.measure) {
    case "time":
      return <TimeBoxes {...props} />;
    case "rounds_reps":
      return <RoundsBoxes {...props} />;
    case "load":
      return <LoadBoxes {...props} />;
    case "count":
      return <CountBox {...props} />;
    case "distance":
      return <DistanceBoxes {...props} />;
    case "pass_fail":
      return <PassFail {...props} />;
    default:
      // Not scored, or a measure this build does not know: nothing to type.
      return null;
  }
}

/** A score's number box: shadcn's input, narrow, its numbers lined up. */
const number = "tabular-nums";

function TimeBoxes({ prefix, type, position, timeLimitMs, current }: BoxesProps) {
  const form = useContext(FormOwner);
  const invalid = useProblem(prefix) ? true : undefined;
  const filled = current ? scoreFields(current) : {};
  const canCap = racesTheCap(type, position, timeLimitMs);
  const [capped, setCapped] = useState(filled.capped === "on");
  const cap = useId();
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-3 text-sm text-muted-foreground">
      {capped ? (
        <>
          <Input
            form={form}
            aria-invalid={invalid}
            name={`${prefix}.reps`}
            inputMode="numeric"
            aria-label="Reps done by the cap"
            defaultValue={filled.reps}
            className={`w-20 ${number}`}
          />
          reps
        </>
      ) : (
        <>
          <Input form={form} aria-invalid={invalid} name={`${prefix}.min`} inputMode="numeric" aria-label="Minutes" defaultValue={filled.min} className={`w-16 ${number}`} />
          min
          <Input form={form} aria-invalid={invalid} name={`${prefix}.sec`} inputMode="decimal" aria-label="Seconds" defaultValue={filled.sec} className={`w-20 ${number}`} />
          sec
        </>
      )}
      {canCap && (
        <div className="flex items-center gap-2 sm:ml-2">
          <Checkbox id={cap} checked={capped} onCheckedChange={(on) => setCapped(on === true)} />
          <Label htmlFor={cap} className="font-normal text-foreground">
            Hit the cap
          </Label>
          {/* The box is named nothing; this sends it, "on" as a ticked box
              did. shadcn's box sends its own only from inside the form
              element, and a part's boxes may name theirs by `form=`. */}
          {capped && <input form={form} type="hidden" name={`${prefix}.capped`} value="on" />}
        </div>
      )}
    </div>
  );
}

function RoundsBoxes({ prefix, current }: BoxesProps) {
  const form = useContext(FormOwner);
  const invalid = useProblem(prefix) ? true : undefined;
  const filled = current ? scoreFields(current) : {};
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <Input form={form} aria-invalid={invalid} name={`${prefix}.rounds`} inputMode="numeric" aria-label="Rounds" defaultValue={filled.rounds} className={`w-20 ${number}`} />
      rounds +
      <Input form={form} aria-invalid={invalid} name={`${prefix}.reps`} inputMode="numeric" aria-label="Reps" defaultValue={filled.reps} className={`w-20 ${number}`} />
      reps
    </div>
  );
}

/** A load, in kg: the app is metric only since 19 October. */
function LoadBoxes({ prefix, current }: BoxesProps) {
  const form = useContext(FormOwner);
  const invalid = useProblem(prefix) ? true : undefined;
  const filled = current ? scoreFields(current) : {};
  return (
    <div className="flex items-center gap-2">
      <Input form={form} aria-invalid={invalid} name={`${prefix}.amount`} inputMode="decimal" aria-label="Load" defaultValue={filled.amount} className={`w-24 ${number}`} />
      <span className="text-sm text-muted-foreground">kg</span>
    </div>
  );
}

function CountBox({ prefix, current }: BoxesProps) {
  const form = useContext(FormOwner);
  const invalid = useProblem(prefix) ? true : undefined;
  const filled = current ? scoreFields(current) : {};
  return (
    <Input form={form} aria-invalid={invalid} name={`${prefix}.amount`} inputMode="numeric" aria-label="Count" defaultValue={filled.amount} className={`w-24 ${number}`} />
  );
}

function DistanceBoxes({ prefix, current }: BoxesProps) {
  const form = useContext(FormOwner);
  const invalid = useProblem(prefix) ? true : undefined;
  const filled = current ? scoreFields(current) : {};
  const [unit, setUnit] = useState(filled.unit ?? "m");
  // Metric — and the unit already recorded, should it be one typed before
  // 19 October in mi, yd, ft or in, so an edit does not lose it.
  const recorded = filled.unit && !(METRIC_DISTANCE_UNITS as readonly string[]).includes(filled.unit) ? [filled.unit] : [];
  const choices: readonly string[] = [...METRIC_DISTANCE_UNITS, ...recorded];
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <Input
        form={form}
        aria-invalid={invalid}
        name={`${prefix}.amount`}
        inputMode={unit === "ft" ? "numeric" : "decimal"}
        aria-label={unit === "ft" ? "Feet" : "Distance"}
        defaultValue={filled.amount}
        className={`w-24 ${number}`}
      />
      <NativeSelect
        form={form}
        name={`${prefix}.unit`}
        aria-label="Unit"
        value={unit}
        onChange={(change) => setUnit(change.target.value)}
      >
        {choices.map((option) => (
          <NativeSelectOption key={option} value={option}>
            {option}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      {unit === "ft" && (
        <>
          <Input form={form} aria-invalid={invalid} name={`${prefix}.inches`} inputMode="decimal" aria-label="Inches" defaultValue={filled.inches} className={`w-20 ${number}`} />
          in
        </>
      )}
    </div>
  );
}

type PassOrFail = "pass" | "fail" | "";

/**
 * Pass, fail, or not done: one of three, as segments where there were
 * radios, posting `result` as they did — "" for not done. The segments'
 * input names no form, so it is sent by the form around it: every form a
 * score is typed in holds its boxes.
 */
function PassFail({ prefix, current }: BoxesProps) {
  const chosen = ((current ? scoreFields(current) : {}).result ?? "") as PassOrFail;
  const [result, setResult] = useState<PassOrFail>(chosen);
  return (
    <SegmentedControl<PassOrFail>
      label="Result"
      name={`${prefix}.result`}
      options={[
        { value: "pass", label: "Pass" },
        { value: "fail", label: "Fail" },
        { value: "", label: "Not done" },
      ]}
      value={result}
      onChange={setResult}
    />
  );
}

/**
 * How a part felt, and a note about it: the athlete's own, beside the result
 * and never part of it. On every part, the skipped and the unscored included,
 * in the one look a feel and a note have everywhere (`FeelAndNote`).
 */
export function NoteFields({
  prefix,
  label,
  current,
}: {
  prefix: string;
  label: string;
  /** What the toggles and the box start from: the part as saved, or as typed before. */
  current: Pick<EntryPart, "feel" | "note"> | undefined;
}) {
  const form = useContext(FormOwner);
  return (
    <div className="flex flex-col gap-3 border-t border-border pt-4">
      <FeelAndNote
        form={form}
        feelName={`${prefix}.feel`}
        noteName={`${prefix}.note`}
        feelLabel={`Part ${label} feel`}
        feel={current?.feel ?? null}
        note={current?.note ?? null}
      />
    </div>
  );
}

/**
 * One part's result: its boxes, its level, and how it felt, with a note.
 * The boxes belong to the form named `form` — the dialog the part is
 * recorded in. With one level for the whole event, the part carries none of
 * its own.
 */
export function PartResultFields({
  part,
  index,
  single = false,
  team,
  form,
}: {
  part: RecordablePart;
  index: number;
  single?: boolean;
  /** On an event's page, what starting a team on a part done in teams needs. */
  team?: TeamStart;
  /** The id of the form the boxes belong to: the dialog's. */
  form: string;
}) {
  if (part.isTeam && team) {
    return (
      <TeamStartFields
        part={part}
        index={index}
        single={single}
        team={team}
        form={form}
      />
    );
  }
  if (part.isTeam) {
    // Away from its event — the log — a team's score is changed on its page.
    return (
      <fieldset aria-label={`${part.label}. ${part.title}`} className="min-w-0">
        <input form={form} type="hidden" name={`parts.${index}.id`} value={part.id} />
        <p className="text-sm text-muted-foreground">Done in teams: each team's score is changed on the team's page.</p>
      </fieldset>
    );
  }
  return <SoloPartFields part={part} index={index} single={single} form={form} />;
}

function SoloPartFields({
  part,
  index,
  single,
  form,
}: {
  part: RecordablePart;
  index: number;
  single: boolean;
  form: string;
}) {
  // Which level is picked, so the Rx+ badge is offered at Rx alone. With one
  // level for the whole event it is picked below the parts, so the badge is
  // offered on every part with levels, and only sent at Rx.
  const [level, setLevel] = useState(part.current?.level ?? "rx");
  return (
    <FormOwner.Provider value={form}>
      <fieldset aria-label={`${part.label}. ${part.title}`} className="min-w-0 space-y-4">
        <input form={form} type="hidden" name={`parts.${index}.id`} value={part.id} />
        {isScored(part) ? (
          <>
            {part.types.map((type, position) => (
              <div key={position} className="space-y-2">
                {part.types.length > 1 && (
                  <p className="text-sm text-muted-foreground">{position === 0 ? type.label : `Then ${type.label.toLowerCase()} (optional)`}</p>
                )}
                <ScoreBoxes
                  prefix={`parts.${index}.scores.${position}`}
                  type={type}
                  position={position}
                  timeLimitMs={part.timeLimitMs}
                  current={part.current?.scores[position]}
                />
              </div>
            ))}
            {part.usesLevels && (
              <LevelAndBadge
                prefix={`parts.${index}`}
                label={part.label}
                single={single}
                level={level}
                onLevel={setLevel}
                rxPlus={part.current?.rxPlus ?? false}
              />
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Not scored — nothing to record but how it felt.</p>
        )}
        {part.late && isScored(part) && (
          <p className="text-xs text-warning">
            Its deadline has passed: a change now stays off the leaderboard until the box verifies it.
          </p>
        )}
        <NoteFields prefix={`parts.${index}`} label={part.label} current={part.current} />
      </fieldset>
    </FormOwner.Provider>
  );
}

/**
 * What starting a team on a part done in teams needs: the space whose members
 * the box suggests, and — when a team typed before is opened again — its id,
 * kept so a retry cannot form it twice, and its people.
 */
export type TeamStart = { slug: string; id?: string; people?: TeamPeople };

/**
 * A team to start on a part done in teams (0027): its score, level and badge,
 * typed as any part's, and its teammates. No note: a teammate's is per team,
 * on the team's page. In an event ranked overall the team is at its creator's
 * one level for the event (0037), chosen below the parts, so it asks none.
 */
function TeamStartFields({
  part,
  index,
  single,
  team,
  form,
}: {
  part: RecordablePart;
  index: number;
  single: boolean;
  team: TeamStart;
  form: string;
}) {
  const [teamId] = useState(() => team.id ?? newTeamId());
  const [level, setLevel] = useState(part.current?.level ?? "rx");
  const prefix = `parts.${index}`;
  return (
    <FormOwner.Provider value={form}>
      <fieldset aria-label={`${part.label}. ${part.title}`} className="min-w-0 space-y-4">
        <input form={form} type="hidden" name={`${prefix}.id`} value={part.id} />
        <input form={form} type="hidden" name={`${prefix}.team.open`} value="1" />
        <input form={form} type="hidden" name={`${prefix}.team.id`} value={teamId} />
        {part.types.map((type, position) => (
          <div key={position} className="space-y-2">
            {part.types.length > 1 && (
              <p className="text-sm text-muted-foreground">{position === 0 ? type.label : `Then ${type.label.toLowerCase()} (optional)`}</p>
            )}
            <ScoreBoxes
              prefix={`${prefix}.scores.${position}`}
              type={type}
              position={position}
              timeLimitMs={part.timeLimitMs}
              current={part.current?.scores[position]}
            />
          </div>
        ))}
        {part.usesLevels && (
          <LevelAndBadge
            prefix={prefix}
            label={part.label}
            single={single}
            level={level}
            onLevel={setLevel}
            rxPlus={part.current?.rxPlus ?? false}
          />
        )}
        {single && part.usesLevels && (
          <p className="text-xs text-muted-foreground">The team is at your level for the event, and so is everybody who joins it.</p>
        )}
        <TeamPeopleBox form={form} prefix={prefix} slug={team.slug} initial={team.people} />
        <ProblemNote name={`${prefix}.team`} />
        {part.late && (
          <p className="text-xs text-warning">
            Its deadline has passed: a team's score recorded now stays off the leaderboard until the box verifies it.
          </p>
        )}
      </fieldset>
    </FormOwner.Provider>
  );
}

/**
 * A part's level, Rx | Scaled, and the Rx+ badge beside it at Rx. Segments
 * rather than radios (decided with Tiago 16 October), posting `level` as they
 * did; their input names no form, so the part's form holds them, as a part's
 * dialog does. With one level for the whole event, asked as recording
 * starts, the part asks none and offers the badge alone.
 */
function LevelAndBadge({
  prefix,
  label,
  single,
  level,
  onLevel,
  rxPlus,
}: {
  prefix: string;
  /** The part's letter, which names the segments for a screen reader. */
  label: string;
  single: boolean;
  level: string;
  onLevel: (level: string) => void;
  rxPlus: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {!single && (
        <div className="min-w-48 flex-1">
          <SegmentedControl<string>
            label={`Part ${label} level`}
            shown="Level"
            name={`${prefix}.level`}
            options={LEVELS.map((option) => ({ value: option.value, label: option.label }))}
            value={level}
            onChange={onLevel}
          />
        </div>
      )}
      {(single || level === "rx") && <RxPlusToggle name={`${prefix}.rxPlus`} on={rxPlus} />}
    </div>
  );
}

/**
 * Which division a result is saved in — the profile's, the one place it is
 * chosen — and where to change it. Without one in the profile, a result that
 * has none either stays private: every board is men's or women's.
 */
export function DivisionNote({
  profileDivision,
  savedDivision = null,
}: {
  profileDivision: string | null;
  savedDivision?: string | null;
}) {
  const toAccount = useAccountLink("division");
  const account = (
    <Link to={toAccount} className="font-medium underline underline-offset-4">
      Account
    </Link>
  );
  if (profileDivision) {
    return (
      <p className="text-sm text-foreground">
        Division: <span className="font-medium">{divisionLabel(profileDivision)}</span>
        <span className="text-muted-foreground"> — from your profile; change it in {account}.</span>
      </p>
    );
  }
  if (savedDivision) {
    return (
      <p className="text-sm text-foreground">
        Division: <span className="font-medium">{divisionLabel(savedDivision)}</span>
        <span className="text-muted-foreground">, as it was saved. Your profile has none now — set it in {account}.</span>
      </p>
    );
  }
  return (
    <p className="rounded-2xl bg-warning-muted px-4 py-3 text-sm text-warning">
      Your profile has no division, so this result stays private: every leaderboard is men's or women's. Set it
      in {account} to share it.
    </p>
  );
}

/**
 * A part's result as its athlete reads it: "4:12 · Rx", tiebreaks after. The
 * level is left off when the entry says it once for the whole event — but not
 * its Rx+ badge, which is the part's own.
 */
export function partResult(part: EntryPart, showLevel = true): string {
  // A team part's result is each team's the athlete is on (0027).
  if (part.isTeam) {
    if (part.teams.length === 0) return "no team";
    return part.teams.map((team) => `team ${teamScore(team)}`).join(" / ");
  }
  if (!part.recorded) return "not recorded";
  const [first, ...tiebreaks] = part.scores;
  const shown = [first?.display ?? ""];
  for (const tiebreak of tiebreaks) if (tiebreak.display) shown.push(`then ${tiebreak.display}`);
  if (showLevel && part.level) shown.push(levelLabel(part.level, part.rxPlus));
  else if (part.rxPlus) shown.push("Rx+");
  return shown.join(" · ");
}

/**
 * The Rx+ badge, switched like a button: what an athlete says about a part
 * done at Rx. It ranks with Rx, so it is no level of its own, and it is not
 * part of the result — switching it moves nothing on the board.
 *
 * shadcn's toggle, drawn as a chip beside the level's segments: pressed or
 * not, ticked when pressed. A hidden input sends it, "on" as a ticked box
 * did, by the form it belongs to — a part's dialog, or the form around it.
 */
export function RxPlusToggle({ name, on }: { name: string; on: boolean }) {
  const form = useContext(FormOwner);
  const [pressed, setPressed] = useState(on);
  return (
    <>
      <Toggle variant="outline" pressed={pressed} onPressedChange={setPressed}>
        {pressed && <Check aria-hidden="true" />}
        Rx+
      </Toggle>
      {pressed && <input form={form} type="hidden" name={name} value="on" />}
    </>
  );
}

/** The Rx+ badge on a result, wherever it shows. */
export function RxPlusBadge() {
  return (
    <Badge variant="outline" className="ml-1 align-middle">
      Rx+
    </Badge>
  );
}

/** Whether the result is on a leaderboard, as its athlete reads it. */
export function SharedBadge({ shared }: { shared: boolean }) {
  return shared ? (
    <Badge variant="secondary" className="bg-success-muted text-success">
      On the leaderboard
    </Badge>
  ) : (
    <Badge variant="secondary">Private</Badge>
  );
}

/** How a part felt, as it shows beside its result. */
export function Feel({ feel }: { feel: string | null }) {
  if (!feel) return null;
  const label = FEELS.find((option) => option.value === feel)?.label ?? feel;
  return (
    <span role="img" aria-label={label} title={label}>
      {feelEmoji(feel)}
    </span>
  );
}

/**
 * A result's line under its part: "— 4:12 · Rx 👍", its note, and where it
 * stands with the space's verifiers — waiting, verified by the space, or
 * changed since it was verified. `pending` marks one not sent yet, which
 * nobody has verified.
 */
export function PartRecorded({
  part,
  showLevel = true,
  spaceName = null,
  pending = null,
  place = null,
}: {
  part: EntryPart | undefined;
  showLevel?: boolean;
  /** The space whose word a verification is, as the entry copied its name. */
  spaceName?: string | null;
  pending?: string | null;
  /** Where it stands on its board, on an event's page: "3rd of 21". */
  place?: { rank: number; of: number } | null;
}) {
  return (
    <div className="text-sm">
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-muted-foreground">Your result</span>{" "}
        <span
          className={
            part?.recorded
              ? "font-heading text-2xl leading-none font-semibold text-foreground tabular-nums"
              : "text-muted-foreground"
          }
        >
          {part ? partResult(part, showLevel) : "not recorded"}
        </span>{" "}
        {place && !pending && (
          <span className="text-muted-foreground">
            {ordinal(place.rank)} of {place.of}
          </span>
        )}{" "}
        <Feel feel={part?.feel ?? null} />
        {pending && <PendingBadge>{pending}</PendingBadge>}
      </p>
      {part?.note && <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{part.note}</p>}
      {part?.recorded && !pending && <PartVerification part={part} spaceName={spaceName} />}
    </div>
  );
}

/** What a part not sent yet says beside it. */
export function PendingBadge({ children }: { children: string }) {
  return (
    <Badge variant="secondary" className="ml-1 bg-warning-muted align-middle text-warning">
      {children}
    </Badge>
  );
}

/**
 * Where a result stands with its space's verifiers. Whether it waits for one
 * is known only on the event's page, where the api reads the deadline; its
 * verification, current or not, is known everywhere.
 */
export function PartVerification({ part, spaceName }: { part: EntryPart; spaceName: string | null }) {
  const verification = part.verification;
  const by = spaceName ? ` by ${spaceName}` : "";
  return (
    <>
      {part.hiddenAt && (
        <p className="mt-0.5 text-xs text-danger">
          Hidden{by}: taken off this part's leaderboard until a moderator puts it back.
        </p>
      )}
      {part.standing === "waiting" && (
        <p className="mt-0.5 text-xs text-warning">
          Waiting for verification: it changed, or was shared, after the deadline, so it stays off the
          leaderboard until the box verifies it.
        </p>
      )}
      {verification?.current && <p className="mt-0.5 text-xs text-success">✓ Verified{by}</p>}
      {verification && !verification.current && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          Changed after it was verified{by}: the verification was of{" "}
          {[
            verification.scores.map((score) => score.display).filter(Boolean).join(", then "),
            verification.level ? levelLabel(verification.level) : "",
            verification.division ? divisionLabel(verification.division) : "",
          ]
            .filter(Boolean)
            .join(" · ")}
          .
        </p>
      )}
      {!verification && part.standing === "unverified" && (
        <p className="mt-0.5 text-xs text-muted-foreground">Not verified yet.</p>
      )}
    </>
  );
}

/** What is true of a whole result: shared or not, its level and division, and when. */
export function ResultSummary({ entry, zone }: { entry: Entry; zone: string }) {
  // Neither the division nor whether the notes go with it: the division is
  // the profile's, and sharing asks what goes each time it is asked
  // (decided with Tiago 13 October).
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <SharedBadge shared={entry.shared} />
      {entry.level && <span>{levelLabel(entry.level)}</span>}
      <span>· done {formatMoment(entry.performedAt, zone)}</span>
    </p>
  );
}

/**
 * What an athlete can do with their result: change it, share or unshare it,
 * delete it — asked first, in the shared question, posted to the page's own
 * action. Sharing needs the space it came from, so it is offered only while
 * that is reachable; unsharing works from anywhere.
 */
export function ResultActions({
  entry,
  canShare,
  busy,
  onEdit,
  onShare,
  logLink = false,
}: {
  entry: Entry;
  canShare: boolean;
  busy: boolean;
  /** Where the whole result is edited at once; left out where each part is. */
  onEdit?: () => void;
  /** Where sharing asks something first; left out, Share sends at once. */
  onShare?: () => void;
  logLink?: boolean;
}) {
  const [deleting, setDeleting] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {onEdit && (
        <Button type="button" variant="outline" size="lg" onClick={onEdit}>
          Edit
        </Button>
      )}
      {/* Private, sharing is what the sheet is for: its main action. */}
      {!entry.shared && canShare && onShare && (
        <Button type="button" size="lg" onClick={onShare} disabled={busy}>
          Share
        </Button>
      )}
      {(entry.shared || (canShare && !onShare)) && (
        <Form method="post">
          <input type="hidden" name="intent" value={entry.shared ? "unshare" : "share"} />
          <input type="hidden" name="entry" value={entry.id} />
          <Button type="submit" variant={entry.shared ? "outline" : "default"} size="lg" disabled={busy}>
            {entry.shared ? "Unshare" : "Share"}
          </Button>
        </Form>
      )}
      <Button type="button" variant="outline" size="lg" onClick={() => setDeleting(true)} disabled={busy}>
        Delete…
      </Button>
      {deleting && (
        <ConfirmDialog
          title="Delete your result?"
          fields={{ intent: "delete-entry", entry: entry.id }}
          confirm="Delete result"
          onClose={() => setDeleting(false)}
        >
          Deleting your result takes it off the leaderboard and out of your log.
        </ConfirmDialog>
      )}
      {logLink && (
        <Button asChild variant="link" size="lg">
          <Link to={`/t/me/entries/${entry.id}`}>In your log</Link>
        </Button>
      )}
    </div>
  );
}
